import React, { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useTranslate } from 'react-translate-plus';

import {
  Button,
  ContextMenu,
  ContextMenuLink,
  Icon,
  icons,
  PositioningPortalWithState,
} from '@hoffmann/pattern-library-2';

import { importConfiguration } from '../../api/simple-project-service/simpleProjectService';
import { Configuration } from '../../interfaces/Configuration';
import { getWindow } from '../../services/windowService';
import { addNotifications } from '../../store/notifications/notifications.actions';
import { NotificationFactory } from '../../utils/factories/notificationFactory';
import { getUrlParamsFromPattern } from '../../utils/router.extensions';

const ExportConfigurationModal = lazy(
  () => import('../../components/ExportImportConfiguration/ExportConfigurationModal'),
);
const ImportConfigurationModal = lazy(
  () => import('../../components/ExportImportConfiguration/ImportConfigurationModal'),
);

const CONFIG_PATH_PATTERN = '/configurator/:configurationId';
const DIALOG_EXPORT = 1;
const DIALOG_IMPORT = 2;

interface Props {
  configuration: Configuration;
}

export const ConfigurationsMenu: React.FC<Props> = ({ configuration }) => {
  const t = useTranslate('NavigationBar');
  const dispatch = useDispatch<any>();
  const [openedDialog, setOpenedDialog] = useState<number>(0);
  const { pathname } = useLocation();
  const { configurationId = '' } = useMemo<Partial<{ configurationId: string }>>(
    () => getUrlParamsFromPattern(CONFIG_PATH_PATTERN, pathname),
    [pathname],
  );

  const closeDialog = useCallback(() => setOpenedDialog(0), []);

  useEffect(() => {
    closeDialog();
  }, [configurationId, closeDialog]);

  const handleError = useCallback(
    (errorMessage: string) => {
      closeDialog();
      dispatch(addNotifications([NotificationFactory.customNegative(errorMessage)]));
    },
    [closeDialog, dispatch],
  );

  const handleImportSubmit = useCallback(
    async (configuration: SimpleProjectServiceAPI.ConfigurationDataDto) => {
      const sessionId = String(Date.now());
      const result = await importConfiguration(configuration, `${t('CONFIGURATION')} 1`, sessionId);

      if (result instanceof Error) {
        return handleError(result.message);
      }

      const wnd = getWindow();
      wnd.open(`${wnd.location.origin}/configurator/${result}`, '_blank');
    },
    [handleError, t],
  );

  const handleNotification = (message: string) => {
    dispatch(addNotifications([NotificationFactory.customPositive(message)]));
  };

  return (
    <>
      <PositioningPortalWithState
        portalContent={({ close }) => (
          <ContextMenu onClose={close}>
            <ContextMenuLink
              label={t('MENU_IMPORT')}
              onClick={() => setOpenedDialog(DIALOG_IMPORT)}
              icon={<Icon source={icons.ArrowBack} />}
            />
            <ContextMenuLink
              label={t('MENU_EXPORT')}
              onClick={() => setOpenedDialog(DIALOG_EXPORT)}
              icon={<Icon source={icons.ArrowForward} />}
            />
          </ContextMenu>
        )}
      >
        {({ open }) => <Button label={t('BUTTON_CONFIGURATIONS')} navigation onClick={open} />}
      </PositioningPortalWithState>
      {openedDialog === DIALOG_EXPORT && (
        <Suspense fallback>
          <ExportConfigurationModal
            configuration={configuration}
            configurationId={configurationId}
            isOpen={openedDialog === DIALOG_EXPORT}
            onClose={closeDialog}
            onError={handleError}
            onNotification={handleNotification}
          />
        </Suspense>
      )}
      {openedDialog === DIALOG_IMPORT && (
        <Suspense fallback>
          <ImportConfigurationModal
            isOpen={openedDialog === DIALOG_IMPORT}
            onClose={closeDialog}
            onSubmit={handleImportSubmit}
            onError={handleError}
          />
        </Suspense>
      )}
    </>
  );
};
